import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch } from '@ngxs/store/operators';
import { Page, PageMetadata } from '@tran-monorepo/interfaces';
import { Location } from '@tran-monorepo/interfaces/applications';
import { EMPTY, Observable } from 'rxjs';
import { catchError, expand, finalize, takeWhile, tap } from 'rxjs/operators';
import { LocationService } from '../../services';
import { LoadAllLocations, LoadLocations, LoadMoreLocations } from './location.actions';

export interface LocationStateModel {
    loading: boolean;
    locations: Location[];
    metadata: PageMetadata | null;
    pageSize: number;
}

@State<LocationStateModel>({
    name: 'locations',
    defaults: {
        loading: false,
        locations: [],
        metadata: null,
        pageSize: 20
    }
})
@Injectable()
export class LocationState {
    private readonly locationService = inject(LocationService);

    @Selector()
    static locations(state: LocationStateModel): Location[] {
        return state.locations;
    }

    @Selector()
    static loading(state: LocationStateModel): boolean {
        return state.loading;
    }

    @Selector()
    static metadata(state: LocationStateModel): PageMetadata | null {
        return state.metadata;
    }

    @Action(LoadLocations)
    load(ctx: StateContext<LocationStateModel>, { payload: { pageSize } }: LoadLocations): Observable<Page<Location>> {
        ctx.patchState({ loading: true, pageSize });

        return this.locationService.getLocations(0, pageSize).pipe(
            tap(({ items: locations, metadata }) =>
                ctx.patchState({
                    locations,
                    metadata
                })
            ),
            catchError(() => {
                ctx.patchState({ locations: [], metadata: null });
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }

    @Action(LoadMoreLocations)
    loadMore(ctx: StateContext<LocationStateModel>): Observable<Page<Location>> | void {
        const { loading, metadata, pageSize } = ctx.getState();

        if (loading || !metadata?.hasNextPage) {
            return;
        }

        const nextPage = (metadata?.currentPage ?? 0) + 1;
        ctx.patchState({ loading: true });

        return this.locationService.getLocations(nextPage, pageSize).pipe(
            tap(({ items: newLocations, metadata }) =>
                ctx.setState(
                    patch<LocationStateModel>({
                        locations: append<Location>(newLocations),
                        metadata
                    })
                )
            ),
            catchError(() => {
                ctx.patchState({ loading: false });
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }

    @Action(LoadAllLocations)
    loadAll(ctx: StateContext<LocationStateModel>): Observable<void> {
        ctx.patchState({ loading: true, locations: [], metadata: null });

        return ctx.dispatch(new LoadLocations({ pageSize: 100 })).pipe(
            expand(() => {
                const { metadata } = ctx.getState();
                return metadata?.hasNextPage ? ctx.dispatch(new LoadMoreLocations()) : EMPTY;
            }),
            takeWhile(() => {
                const { metadata } = ctx.getState();
                return metadata?.hasNextPage ?? false;
            }, true),
            catchError(() => {
                ctx.patchState({ loading: false, locations: [], metadata: null });
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }
}
